import { get, reduce, findKey } from 'lodash';

import {
  IS_HEADER_ENABLED_PATH,
  IS_LOGIN_WIDGET_ENABLED_PATH,
  IS_RCE_IMAGE_ENABLED_PATH,
  IS_RCE_HTML_ENABLED_PATH,
  IS_RCE_VIDEO_ENABLED_PATH,
  IS_RCE_DIVIDER_ENABLED_PATH,
  IS_VIEWS_COUNT_ENABLED_PATH,
  IS_LIST_HASHTAGS_ENABLED_PATH,
  IS_SOCIAL_SHARING_LINKS_ENABLED_PATH,
  IS_MAIN_PAGE_HEADER_ENABLED_PATH,
  FORUM_STRUCTURE_PATH,
  HEADER_HEIGHT_PATH,
  MAIN_PAGE_HEADER_HEIGHT_PATH,
  CATEGORY_TEXT_ALIGNMENT_PATH,
  MAIN_PAGE_TEXT_ALIGNMENT_PATH,
  POST_SORTING_PATH,
  IS_SHOW_SUBCATEGORIES_ENABLED_PATH,
} from '../constants/wix-params';
import {
  getAppSettings,
  getAppSettingsValue,
} from '../../common/store/app-settings/app-settings-selectors';
import { isMemberAreaActive } from './member-area-selectors';
import { getCategories } from './categories-selectors';
import { PLUGINS } from '../components/rich-content-editor/plugins-constants';
import { TEXT_ALIGNMENT_CENTER } from '../constants/text-alignment';
import { FORUM_STRUCTURE_REGULAR } from '../../common/constants/forum-structure-toggle-types';
import { mapToAppSettings, SORT_BY_LAST_ACTIVITY } from '../constants/sorting';
import { IS_FOOTER_POSTS_ENABLED_PATH } from '../../../app/constants/wix-params';

export const getForumData = state => get(getAppSettings(state), 'data.forum', {});

export const getSiteColors = state =>
  reduce(
    getAppSettings(state).colorPresets,
    (acc, { reference, value }) => {
      acc[reference] = value;
      return acc;
    },
    {},
  );

export const getIsHeaderEnabled = state => {
  return getAppSettingsValue({ state, key: IS_HEADER_ENABLED_PATH, fallback: true });
};

export const getHeaderHeight = state => {
  return getAppSettingsValue({ state, key: HEADER_HEIGHT_PATH, fallback: 250 });
};

export const getMainPageHeaderHeight = state => {
  return getAppSettingsValue({
    state,
    key: MAIN_PAGE_HEADER_HEIGHT_PATH,
    fallback: getHeaderHeight(state),
  });
};

export const getCategoryTextAlignment = state => {
  return getAppSettingsValue({
    state,
    key: CATEGORY_TEXT_ALIGNMENT_PATH,
    fallback: TEXT_ALIGNMENT_CENTER,
  });
};

export const getMainPageHeaderTextAlignment = state => {
  return getAppSettingsValue({
    state,
    key: MAIN_PAGE_TEXT_ALIGNMENT_PATH,
    fallback: getCategoryTextAlignment(state),
  });
};

export const getIsMainPageHeaderEnabled = state => {
  return getAppSettingsValue({
    state,
    key: IS_MAIN_PAGE_HEADER_ENABLED_PATH,
    fallback: getIsHeaderEnabled(state),
  });
};

export const getIsShowSubcategoriesEnabled = state => {
  return getAppSettingsValue({
    state,
    key: IS_SHOW_SUBCATEGORIES_ENABLED_PATH,
    fallback: false,
  });
};

export const getIsForumStructureRegular = state => {
  return (
    getAppSettingsValue({ state, key: FORUM_STRUCTURE_PATH, fallback: FORUM_STRUCTURE_REGULAR }) ===
    FORUM_STRUCTURE_REGULAR
  );
};

// returns string value of sorting constant, example: 'sortByBestMach'
export const getPostSorting = state => {
  const numberValue = getAppSettingsValue({
    state,
    key: POST_SORTING_PATH,
    fallback: mapToAppSettings[SORT_BY_LAST_ACTIVITY],
  });
  return findKey(mapToAppSettings, s => s === String(numberValue));
};

export const getIsMainPageEnabled = state => {
  const isForumRegular = getIsForumStructureRegular(state);
  const totalCategories = getCategories(state).length;
  return isForumRegular && totalCategories > 1;
};

export const getIsCategorySelectEnabled = state => {
  const isForumFeed = !getIsForumStructureRegular(state);
  const totalCategories = getCategories(state).length;
  return isForumFeed && totalCategories > 1;
};

export const getCustomAppSettings = state =>
  getAppSettingsValue({ state, key: 'custom', fallback: {} });

export const getIsLoginWidgetEnabled = state =>
  getAppSettingsValue({
    state,
    key: IS_LOGIN_WIDGET_ENABLED_PATH,
    fallback: !isMemberAreaActive(state),
  });

export const getIsViewsCountEnabled = state =>
  getAppSettingsValue({
    state,
    key: IS_VIEWS_COUNT_ENABLED_PATH,
    fallback: true,
  });

export const getIsListHashtagsEnabled = state =>
  getAppSettingsValue({
    state,
    key: IS_LIST_HASHTAGS_ENABLED_PATH,
    fallback: true,
  });

export const getIsSocialSharingLinksEnabled = state =>
  getAppSettingsValue({
    state,
    key: IS_SOCIAL_SHARING_LINKS_ENABLED_PATH,
    fallback: true,
  });

export const getEnabledRcePlugins = state =>
  [
    getAppSettingsValue({ state, key: IS_RCE_IMAGE_ENABLED_PATH, fallback: true })
      ? PLUGINS.IMAGE
      : null,
    getAppSettingsValue({ state, key: IS_RCE_VIDEO_ENABLED_PATH, fallback: true })
      ? PLUGINS.VIDEO
      : null,
    getAppSettingsValue({ state, key: IS_RCE_DIVIDER_ENABLED_PATH, fallback: true })
      ? PLUGINS.DIVIDER
      : null,
    getAppSettingsValue({ state, key: IS_RCE_HTML_ENABLED_PATH, fallback: false })
      ? PLUGINS.HTML
      : null,
  ].filter(p => p !== null);

export const getIsFooterPostsEnabled = state => {
  return getAppSettingsValue({ state, key: IS_FOOTER_POSTS_ENABLED_PATH, fallback: true });
};
